/**
 * useStreakSystem Hook
 * 
 * Manages daily learning streaks with rewards and streak tracking.
 * Tracks consecutive days of learning activity.
 * 
 * @module useStreakSystem
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGameProgress } from './useGameProgress';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { logger } from '../utils/logger';

/**
 * Streak milestone rewards
 */
export const STREAK_MILESTONES = {
  3: { reward: 'bronze', points: 10 },
  7: { reward: 'silver', points: 25 },
  14: { reward: 'gold', points: 50 },
  30: { reward: 'platinum', points: 100 },
  60: { reward: 'diamond', points: 250 },
  100: { reward: 'legendary', points: 500 }
};

/**
 * Hook for managing learning streaks
 * 
 * @param {Object} [options] - Configuration options
 * @param {boolean} [options.autoUpdate=true] - Automatically update streak on activity
 * @returns {Object} Streak state and management functions
 * 
 * @example
 * const { streak, lastPlayedDate, updateStreak, streakMilestone } = useStreakSystem();
 */
export const useStreakSystem = (options = {}) => {
  const { autoUpdate = true } = options;
  const { progress, saveProgress } = useGameProgress();
  
  // Storage and logger are now statically imported
  
  const [streak, setStreak] = useState(() => {
    return progress?.streak || 0;
  });

  const [lastPlayedDate, setLastPlayedDate] = useState(() => {
    return progress?.lastPlayed ? new Date(progress.lastPlayed) : null;
  });

  /**
   * Get current date as YYYY-MM-DD string
   * 
   * @returns {string} Current date string
   */
  const getTodayString = useCallback(() => {
    return new Date().toISOString().split('T')[0];
  }, []);

  /**
   * Get date string from Date object
   * 
   * @param {Date} date - Date object
   * @returns {string} Date string
   */
  const getDateString = useCallback((date) => {
    return date.toISOString().split('T')[0];
  }, []);

  /**
   * Calculate days difference between two dates
   * 
   * @param {Date} date1 - First date
   * @param {Date} date2 - Second date
   * @returns {number} Days difference
   */
  const getDaysDifference = useCallback((date1, date2) => {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round((date2 - date1) / oneDay);
  }, []);

  /**
   * Update streak based on last played date
   */
  const updateStreak = useCallback(() => {
    const today = new Date();
    const todayString = getTodayString();
    
    if (!lastPlayedDate) {
      // First time playing - start streak
      const newStreak = 1;
      setStreak(newStreak);
      setLastPlayedDate(today);
      
      // Save to progress
      saveProgress({
        ...progress,
        streak: newStreak,
        lastPlayed: today.toISOString()
      });
      
      logger.debug('Streak started: 1 day');
      return { newStreak, isNew: true };
    }

    const lastPlayedString = getDateString(lastPlayedDate);
    const daysDifference = getDaysDifference(lastPlayedDate, today);

    if (lastPlayedString === todayString) {
      // Already played today - maintain streak
      return { newStreak: streak, isNew: false };
    } else if (daysDifference === 1) {
      // Played yesterday - continue streak
      const newStreak = streak + 1;
      setStreak(newStreak);
      setLastPlayedDate(today);
      
      // Save to progress
      saveProgress({
        ...progress,
        streak: newStreak,
        lastPlayed: today.toISOString()
      });
      
      logger.debug(`Streak continued: ${newStreak} days`);
      
      // Check for milestone
      const milestone = checkStreakMilestone(newStreak);
      if (milestone) {
        return { newStreak, isNew: true, milestone };
      }
      
      return { newStreak, isNew: true };
    } else {
      // Streak broken - reset to 1
      const newStreak = 1;
      setStreak(newStreak);
      setLastPlayedDate(today);
      
      // Save to progress
      saveProgress({
        ...progress,
        streak: newStreak,
        lastPlayed: today.toISOString()
      });
      
      logger.debug('Streak broken, reset to 1 day');
      return { newStreak, isNew: true, streakBroken: true };
    }
  }, [streak, lastPlayedDate, progress, saveProgress, getTodayString, getDateString, getDaysDifference]);

  /**
   * Check if current streak reached a milestone
   * 
   * @param {number} currentStreak - Current streak count
   * @returns {Object|null} Milestone info or null
   */
  const checkStreakMilestone = useCallback((currentStreak) => {
    if (STREAK_MILESTONES[currentStreak]) {
      return {
        days: currentStreak,
        ...STREAK_MILESTONES[currentStreak]
      };
    }
    return null;
  }, []);

  /**
   * Get current streak milestone (if any)
   */
  const streakMilestone = useMemo(() => {
    return checkStreakMilestone(streak);
  }, [streak, checkStreakMilestone]);

  /**
   * Get next milestone
   */
  const nextMilestone = useMemo(() => {
    const milestones = Object.keys(STREAK_MILESTONES).map(Number).sort((a, b) => a - b);
    const next = milestones.find(m => m > streak);
    
    if (next) {
      return {
        days: next,
        daysRemaining: next - streak,
        ...STREAK_MILESTONES[next]
      };
    }
    
    return null;
  }, [streak]);

  /**
   * Get streak statistics
   */
  const streakStats = useMemo(() => {
    // Use localStorage directly to avoid module import
    let longestStreak = streak;
    let totalDays = 0;
    try {
      const savedLongest = localStorage.getItem(`emmy-${STORAGE_KEYS.LONGEST_STREAK}`);
      if (savedLongest) longestStreak = Math.max(parseInt(savedLongest) || 0, streak);
      const savedTotal = localStorage.getItem(`emmy-${STORAGE_KEYS.TOTAL_LEARNING_DAYS}`);
      if (savedTotal) totalDays = parseInt(savedTotal) || 0;
    } catch (e) {
      // Silently fail
    }
    
    return {
      current: streak,
      longest: longestStreak,
      totalDays,
      milestone: streakMilestone,
      nextMilestone
    };
  }, [streak, streakMilestone, nextMilestone]);

  /**
   * Update longest streak record
   */
  useEffect(() => {
    if (streak > 0) {
      const longest = storage.get(STORAGE_KEYS.LONGEST_STREAK) || 0;
      if (streak > longest) {
        storage.set(STORAGE_KEYS.LONGEST_STREAK, streak);
      }
    }
  }, [streak]);

  /**
   * Auto-update streak when activity is detected
   */
  useEffect(() => {
    if (autoUpdate) {
      // Update streak when component mounts or progress changes
      const hasActivity = progress.totalScore > 0 || 
                         Object.keys(progress.completedSubjects || {}).length > 0;
      
      if (hasActivity) {
        updateStreak();
      }
    }
  }, [autoUpdate, progress.totalScore, progress.completedSubjects, updateStreak]);

  return {
    streak,
    lastPlayedDate,
    updateStreak,
    streakMilestone,
    nextMilestone,
    streakStats,
    checkStreakMilestone
  };
};

